/**
 * The changes one cell of a table went through, read from the journal.
 *
 * The journal keeps what a block said after each change, so a cell's own
 * history is the places where its value differs from one state to the next.
 * A cell is found again in an older state the way a reader would: its row by
 * the label in the first column, its column by the heading, so a row added in
 * between does not shift it onto another figure.
 *
 * Pure. Entries in, the cell's changes out.
 */

import type { JournalEntry } from '@/domain/journal'
import { readSection } from '@/domain/report'
import type { TableSection } from '@/domain/section'

/** Where a cell is, said by what labels it rather than by its place. */
export interface CellTarget {
  /** 0 for the header, a body row's place plus one otherwise. */
  readonly line: number
  readonly column: number
  /** The first cell of its row; empty for the header. */
  readonly rowLabel: string
  /** Which row of that label it is, counting from the top: 0 for the first. */
  readonly occurrence: number
  readonly columnLabel: string
}

/** One change of the cell: what it said, what it says after, and who did it. */
export interface CellEvent {
  readonly author: string
  /** When the person stopped changing the block; the change was made by then. */
  readonly at: string
  /** Null where the cell did not exist yet. */
  readonly from: string | null
  /** Null where the cell is gone. */
  readonly to: string | null
}

/** The target of a cell of this table, as it is drawn. */
export function targetOf(table: TableSection, line: number, column: number): CellTarget {
  const columnLabel = table.header[column]?.value ?? ''
  if (line === 0) return { line, column, rowLabel: '', occurrence: 0, columnLabel }

  const rowLabel = table.rows[line - 1]?.[0]?.value ?? ''
  const occurrence = table.rows
    .slice(0, line - 1)
    .filter((cells) => (cells[0]?.value ?? '') === rowLabel).length
  return { line, column, rowLabel, occurrence, columnLabel }
}

/** The column with this heading; the same place where the heading is empty. */
function columnIn(table: TableSection, target: CellTarget): number | undefined {
  if (target.columnLabel === '') {
    return target.column < Math.max(table.header.length, 1) ? target.column : undefined
  }
  const at = table.header.findIndex((cell) => cell.value === target.columnLabel)
  return at < 0 ? undefined : at
}

/** What the cell says in a table, or null where the table has no such cell. */
export function valueIn(table: TableSection, target: CellTarget): string | null {
  const column = columnIn(table, target)
  if (column === undefined) return null
  if (target.line === 0) return table.header[column]?.value ?? null

  let seen = 0
  for (const cells of table.rows) {
    if ((cells[0]?.value ?? '') !== target.rowLabel) continue
    if (seen === target.occurrence) return cells[column]?.value ?? null
    seen += 1
  }
  return null
}

function tableOf(content: JournalEntry['content']): TableSection | null {
  if (!content) return null
  const section = readSection(content)
  return section?.kind === 'table' ? section : null
}

/**
 * The changes of a cell, the newest first.
 *
 * `entries` come newest first, each with what the block said after it. Between
 * an entry and the next older one, the cell changed if it reads differently; the
 * entry's author is who changed it. The oldest entry is compared with what the
 * block said before, where that was kept. An entry whose content was not kept
 * cannot say, and is passed over.
 */
export function cellTrail(
  entries: readonly JournalEntry[],
  target: CellTarget,
): readonly CellEvent[] {
  const events: CellEvent[] = []

  entries.forEach((entry, at) => {
    if (entry.action === 'moved' || entry.action === 'removed') return

    const now = tableOf(entry.content)
    if (!now) return

    const older = entries[at + 1]
    const was = older ? tableOf(older.content) : tableOf(entry.before)
    // Nothing to compare with: the block's first state says nothing of the cell.
    if (!was) return

    const from = valueIn(was, target)
    const to = valueIn(now, target)
    if (from === to) return
    events.push({ author: entry.author, at: entry.endedAt, from, to })
  })

  return events
}
